import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { headingFade, springEntrance, stagger, useCardTilt } from "@/lib/motion";

const ideas = [
  {
    category: "Customer Success",
    title: "The After-Hours Dispatch Agent",
    description: "An intelligent receptionist for regional HVAC and plumbing companies. It answers calls, qualifies true emergencies based on your specific criteria, and autonomously texts the on-call technician with full context and customer history.",
  },
  {
    category: "Operations",
    title: "Autonomous Supply Chain Monitor",
    description: "A background agent that continuously tracks vendor shipments against production schedules. When a delay is detected, it automatically alerts the floor manager and drafts contingency communications for affected clients.",
  },
  {
    category: "Sales",
    title: "Strategic RFP Responder",
    description: "Stop spending days on proposals. This system parses massive RFP documents, searches your company's historical winning bids, and drafts a highly specific, technically accurate first draft for your sales team to review.",
  },
  {
    category: "Finance & Back-Office",
    title: "Intelligent Invoice Reconciliation",
    description: "A financial workflow that ingests dozens of unformatted vendor invoices daily, extracts line items, matches them against purchase orders in your ERP, and flags only the exceptions for human review.",
  },
  {
    category: "Marketing",
    title: "Hyper-Local Content Engine",
    description: "An agentic marketing pipeline that monitors local market trends, generates highly relevant blog posts and social updates tailored to your specific service areas, and schedules them for approval.",
  },
  {
    category: "The Micro-Business",
    title: "The Agentic Solo-Practice",
    description: "A completely unified operating system for independent consultants or specialized firms. It handles scheduling, pre-meeting briefs, automated follow-ups, and baseline client reporting—acting as a tireless chief of staff.",
  },
];

function IdeaCard({ idea }: { idea: typeof ideas[number] }) {
  const tiltRef = useCardTilt(3);
  return (
    <motion.div variants={springEntrance} className="will-change-transform">
      <div ref={tiltRef} className="h-full transition-transform duration-150 ease-out [transform-style:preserve-3d]">
        <Card className="bg-card h-full border-border/50 hover:border-primary/40 hover:ring-2 hover:ring-orange-400/30 transition-all duration-200 group relative overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background:
                "radial-gradient(circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,0.07), transparent 40%)",
            }}
          />
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              <span className="text-xs font-mono text-primary uppercase tracking-wider">{idea.category}</span>
            </div>
            <h4 className="text-xl font-display font-bold text-foreground mb-3">{idea.title}</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">{idea.description}</p>
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
}

export function Ideas() {
  return (
    <section id="ideas" className="py-24 md:py-32 relative bg-secondary/30 border-y border-border">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div
          className="max-w-3xl mb-16 text-center mx-auto"
          variants={headingFade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          <h2 className="text-primary font-mono text-sm tracking-widest uppercase mb-3">What We Build</h2>
          <h3 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-6">
            Agentic concepts. Real outcomes.
          </h3>
          <p className="text-lg text-muted-foreground">
            We don't build toys. We build focused, outcome-driven systems that solve specific operational bottlenecks in your business. Here is what that looks like in practice.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={stagger(0.06)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {ideas.map((idea, index) => (
            <IdeaCard key={index} idea={idea} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
